import { Box, Tooltip } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { I18N_KEYS } from '@/language/keys';
import { FF_SUBFLOWS_SAVED_NODES } from '@/consts/featureFlags';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { DotsThreeIcon } from '@/UI/Icons/DotsThreeIcon';
import { LockClosedIcon } from '@/UI/Icons/LockClosedIcon';
import { EditableLabel } from '@/components/Recipe/FlowComponents/Editor/Designer/LayerPanel/EditableLabel';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { FlexCenVer } from '@/UI/styles';
import { color } from '@/colors';
import { getOS } from '@/utils/general';
import { useIsNodeInRunningBatch } from '@/components/Recipe/RunFlow/batches.store';
import { useEffectiveIsLocked, useFlowView, useIsNodeInGroup, useNodeNullable } from '@/components/Recipe/FlowContext';
import { SaveNodeModal } from '@/components/SubflowTemplates/SaveNodeModal';
import { SubflowTemplateType } from '@/types/api/subflowTemplate';
import { useNodeActions } from '../useNodeActions';
import type { MenuAction } from '@/components/Menu/Actions';
import type { BaseNodeData } from '@/types/node';

const MENU_POSITION = {
  vertical: 'top',
  horizontal: 'left',
} as const;

interface DynamicNodeTitleProps {
  additionalMenu?: MenuAction[];
  data: BaseNodeData;
  externalOpenPosition?: { x: number; y: number } | null;
  displayName?: string;
  hideBody?: boolean;
  icon?: React.ReactNode;
  id: string;
  menuHeader?: React.ReactNode;
  onExternalClose?: () => void;
  onRename?: (newName: string) => void;
  showFullNode: boolean;
  titleRight?: React.ReactNode;
}
export const DynamicNodeTitle = ({
  additionalMenu = [],
  data,
  externalOpenPosition,
  displayName,
  hideBody = false,
  icon,
  id,
  menuHeader,
  onExternalClose,
  onRename,
  showFullNode,
  titleRight,
}: DynamicNodeTitleProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const open = Boolean(anchorEl) || Boolean(externalOpenPosition);

  const role = useUserWorkflowRole();
  const { deleteNode, duplicateNode, toggleLockNode } = useNodeActions();
  const isNodeInRunningBatch = useIsNodeInRunningBatch(id);
  const isLocked = useEffectiveIsLocked(id);
  const node = useNodeNullable(id);
  const isParentLocked = useEffectiveIsLocked(node?.parentId);
  const isNodeInGroup = useIsNodeInGroup(id);
  const flowView = useFlowView();
  const title = displayName || data.name;

  const handleDelete = useCallback(() => {
    deleteNode(id);
  }, [deleteNode, id]);

  const handleDuplicate = useCallback(() => {
    if (id) {
      duplicateNode(id);
    }
  }, [duplicateNode, id]);

  const handleRename = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleToggleLock = useCallback(() => {
    toggleLockNode(id);
  }, [toggleLockNode, id]);

  const handleRemoveFromGroup = useCallback(() => {
    flowView.removeNodesFromGroup([id]);
  }, [flowView, id]);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    onExternalClose?.();
  }, [onExternalClose]);

  const handleRenameSubmit = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed || trimmed === title) {
        return;
      }
      flowView.renameNodeDisplayName(id, trimmed);
      onRename?.(trimmed);
    },
    [title, flowView, id, onRename],
  );

  const ff_isSubflowsSavedNodes = useFeatureFlagEnabled(FF_SUBFLOWS_SAVED_NODES);
  const nodeType = data.model ? SubflowTemplateType.SavedModel : SubflowTemplateType.SavedNode;
  const saveLabel =
    nodeType === SubflowTemplateType.SavedModel
      ? I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_MODEL
      : I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_NODE;
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const menu: MenuAction[] = useMemo(() => {
    const result: MenuAction[] = [
      ...(ff_isSubflowsSavedNodes
        ? [
            {
              name: t(saveLabel),
              action: () => setIsSaveModalOpen(true),
            },
          ]
        : []),
      {
        name: `${t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.DUPLICATE)}`,
        action: handleDuplicate,
        shortcut: `${getOS() === 'Mac' ? 'cmd' : 'ctrl'}+d`,
      },
      {
        name: `${t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.RENAME)}`,
        action: handleRename,
      },
      {
        name: isLocked
          ? isParentLocked
            ? `${t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.UNLOCK_GROUP_FIRST)}`
            : `${t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.UNLOCK)}`
          : `${t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.LOCK)}`,
        action: handleToggleLock,
        disabled: isNodeInRunningBatch || isParentLocked,
      },
    ];

    if (isNodeInGroup) {
      result.push(
        {
          type: 'divider',
        },
        {
          name: `${t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.REMOVE_FROM_GROUP)}`,
          action: handleRemoveFromGroup,
          disabled: isNodeInRunningBatch || isLocked || isParentLocked,
        },
      );
    }

    result.push(
      {
        type: 'divider',
      },
      {
        name: `${t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.DELETE)}`,
        action: handleDelete,
        shortcut: 'delete / backspace',
        disabled: isNodeInRunningBatch || isLocked,
      },
      ...additionalMenu,
    );

    return result;
  }, [
    additionalMenu,
    ff_isSubflowsSavedNodes,
    saveLabel,
    isLocked,
    isParentLocked,
    isNodeInGroup,
    handleDelete,
    handleDuplicate,
    handleRename,
    handleToggleLock,
    handleRemoveFromGroup,
    isNodeInRunningBatch,
    t,
  ]);

  return (
    <>
      <FlexCenVer
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          mb: !hideBody ? 1.5 : 0,
        }}
      >
        <FlexCenVer sx={{ maxWidth: '360px', gap: 1 }}>
          {icon ?? null}
          {role === 'editor' ? (
            <Box
              component="span"
              sx={{ flex: 1, minWidth: 0 }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <EditableLabel
                data-testid={`node-title-text-${id}`}
                className={`dynamic-node-title-label${isEditing ? ' nodrag' : ''}`}
                value={title}
                onSubmit={handleRenameSubmit}
                open={isEditing}
                onClose={() => setIsEditing(false)}
                variant="body-lg-rg"
                onEditingChange={setIsEditing}
                sx={{
                  mr: 1,
                  transition: 'all 0.25s ease-in-out',
                  color: showFullNode ? color.White100 : color.White80_T,
                  pointerEvents: 'auto',
                  display: 'block',
                }}
              />
            </Box>
          ) : (
            <EllipsisText
              data-testid={`node-title-text-${id}`}
              variant="body-lg-rg"
              sx={{
                mr: 1,
                flex: 1,
                minWidth: 0,
                transition: 'all 0.25s ease-in-out',
                color: showFullNode ? color.White100 : color.White80_T,
                cursor: showFullNode ? 'grab' : undefined,
                pointerEvents: 'auto',
              }}
            >
              {title}
            </EllipsisText>
          )}
        </FlexCenVer>
        <FlexCenVer sx={{ gap: 1.25 }}>
          {titleRight ?? null}
          {role === 'guest' && (
            <span>
              <AppIconButton disabled>
                <LockClosedIcon width={18} height={18} />
              </AppIconButton>
            </span>
          )}
          {role === 'editor' && menu && menu.length > 0 && (
            <>
              {isLocked ? (
                <Tooltip
                  title={t(
                    isParentLocked
                      ? I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.UNLOCK_GROUP_FIRST
                      : I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.UNLOCK,
                  )}
                  enterDelay={500}
                  enterNextDelay={500}
                >
                  <span>
                    <AppIconButton onClick={handleToggleLock} disabled={isNodeInRunningBatch || isParentLocked}>
                      <LockClosedIcon width={18} height={18} />
                    </AppIconButton>
                  </span>
                </Tooltip>
              ) : null}
              <AppIconButton data-testid={`node-menu-trigger-${id}`} onClick={(e) => setAnchorEl(e.currentTarget)}>
                <DotsThreeIcon />
              </AppIconButton>
              <AppContextMenu
                data-testid="node-menu"
                anchorEl={anchorEl}
                anchorOrigin={MENU_POSITION}
                header={menuHeader}
                items={menu}
                mouseX={externalOpenPosition?.x}
                mouseY={externalOpenPosition?.y}
                onClose={handleMenuClose}
                open={open}
                transformOrigin={MENU_POSITION}
                width="240px"
              />
            </>
          )}
        </FlexCenVer>
      </FlexCenVer>
      {isSaveModalOpen && <SaveNodeModal nodeId={id} nodeType={nodeType} onClose={() => setIsSaveModalOpen(false)} />}
    </>
  );
};
